/**
 * AnalyticsManager — singleton that orchestrates the event pipeline.
 * Handles consent gating, PII scrubbing, batching, and fan-out to providers.
 */

import { providerRegistry } from './providers';
// Side-effect imports: register built-in providers with the registry
import './providers/null';
import './providers/console';
import './providers/umami';
import { getEventTier, isValidEvent } from './schema';
import { hasConsent, setConsent as saveConsent, withdrawConsent, getConsent } from './consent';
import { scrubProperties } from './scrubber';

class AnalyticsManager {
  constructor() {
    this.providers = [];
    this.initialized = false;
    this.debug = false;
    this.piiScrubbing = true;
    this.globalProperties = {};
  }

  /**
   * Initialize the analytics manager with config.
   * @param {Object} config - Analytics configuration
   * @param {boolean} config.enabled - Master switch
   * @param {boolean} config.debug - Log all events to console
   * @param {boolean} config.pii_scrubbing - Scrub PII from properties
   * @param {Array} config.providers - Provider configs [{name, enabled, config}]
   */
  init(config) {
    if (!config?.enabled) return;
    if (this.initialized) return;

    this.debug = config.debug || false;
    this.piiScrubbing = config.pii_scrubbing !== false;
    this.globalProperties = config.global_properties || {};

    // Initialize enabled providers
    for (const pcfg of (config.providers || [])) {
      if (!pcfg.enabled) continue;
      const ProviderClass = providerRegistry.get(pcfg.name);
      if (!ProviderClass) {
        console.warn(`[analytics] unknown provider: ${pcfg.name}`);
        continue;
      }
      const provider = new ProviderClass();
      provider.initialize(pcfg.config || {});
      this.providers.push(provider);
    }

    // In debug mode, always add console provider
    if (this.debug && !this.providers.find(p => p.name === 'console')) {
      const ConsoleClass = providerRegistry.get('console');
      if (ConsoleClass) {
        const cp = new ConsoleClass();
        cp.initialize({});
        this.providers.push(cp);
      }
    }

    this.initialized = true;

    if (this.debug) {
      console.log(`[analytics] initialized with ${this.providers.length} providers`);
    }
  }

  /**
   * Track an event with properties.
   * Respects consent tier, scrubs PII, enriches with global props.
   */
  async track(event, properties = {}) {
    if (!this.initialized) return;
    if (!isValidEvent(event)) {
      if (this.debug) console.warn(`[analytics] unknown event: ${event}`);
      return;
    }

    const tier = getEventTier(event);
    if (!hasConsent(tier)) return;

    // Enrich with global properties
    let enriched = {
      ...this.globalProperties,
      ...properties,
      platform: 'web',
      env: process.env.REACT_APP_ENV || 'development',
      timestamp: new Date().toISOString(),
    };

    // Scrub PII
    if (this.piiScrubbing) {
      enriched = await scrubProperties(enriched);
    }

    // Fan-out to all providers
    for (const provider of this.providers) {
      try {
        provider.track(event, enriched);
      } catch (err) {
        if (this.debug) console.error(`[analytics] ${provider.name} track error:`, err);
      }
    }
  }

  /**
   * Identify a user with traits.
   */
  identify(userId, traits = {}) {
    if (!this.initialized) return;
    for (const provider of this.providers) {
      try {
        provider.identify(userId, traits);
      } catch (err) {
        if (this.debug) console.error(`[analytics] ${provider.name} identify error:`, err);
      }
    }
  }

  /**
   * Track a page view.
   */
  pageView(page, properties = {}) {
    this.track('page_view', { page, ...properties });
  }

  /**
   * Track a revenue event.
   */
  revenue(event, amountPaise, currency = 'INR', properties = {}) {
    this.track(event, { amount: amountPaise, currency, ...properties });
  }

  /**
   * Track time on page (sent via beacon on unload).
   */
  timing(page, durationMs) {
    this.track('page_time', { page, duration_ms: durationMs });
  }

  /**
   * Update consent state.
   */
  setConsent(consent) {
    const state = saveConsent(consent);
    for (const provider of this.providers) {
      try {
        provider.setConsent(state);
      } catch {
        // Ignore
      }
    }
    // Track consent change (Tier 0 — always allowed)
    this.track(consent.tier1 || consent.tier2 ? 'consent_granted' : 'consent_withdrawn', {
      tier1: consent.tier1,
      tier2: consent.tier2,
    });
    return state;
  }

  /**
   * Get current consent state.
   */
  getConsent() {
    return getConsent();
  }

  /**
   * Withdraw all consent, clear analytics data.
   */
  reset() {
    withdrawConsent();
    for (const provider of this.providers) {
      try {
        provider.reset();
      } catch {
        // Ignore
      }
    }
  }

  /**
   * Flush all providers.
   */
  flush() {
    for (const provider of this.providers) {
      try {
        provider.flush();
      } catch {
        // Ignore
      }
    }
  }

  /**
   * Shut down all providers.
   */
  shutdown() {
    this.flush();
    for (const provider of this.providers) {
      try {
        provider.shutdown();
      } catch {
        // Ignore
      }
    }
    this.providers = [];
    this.initialized = false;
  }
}

// Singleton instance
const analytics = new AnalyticsManager();
export default analytics;
